import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { Fine } from "@/types/database";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";

const FINE_SELECT = `
  *,
  unit:units!fines_unit_id_fkey(*),
  host:hosts!fines_host_id_fkey(
    *,
    profile:profiles!hosts_user_id_fkey(*)
  ),
  booking:bookings(id, guest_name, check_in, check_out)
`;

/** Fines for the active organization — used on the council/PM-facing Fines page. */
export function useOrgFines() {
  const { activeOrgId } = useAuth();

  return useQuery({
    queryKey: ["fines", "org", activeOrgId],
    enabled: !!activeOrgId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("fines")
        .select(FINE_SELECT)
        .eq("organization_id", activeOrgId!)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as unknown as Fine[];
    },
  });
}

/** Fines for the signed-in host — used on the host-facing Fines page. */
export function useMyFines() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["fines", "mine", user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      const { data: host } = await supabase
        .from("hosts")
        .select("id")
        .eq("user_id", user!.id)
        .maybeSingle();
      if (!host) return [];

      const { data, error } = await supabase
        .from("fines")
        .select(FINE_SELECT)
        .eq("host_id", host.id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as unknown as Fine[];
    },
  });
}

/** Issues a fine via the issue-fine Edge Function (creates the row + a Stripe Checkout link). */
export function useIssueFine() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { activeOrgId } = useAuth();

  return useMutation({
    mutationFn: async (fine: {
      host_id: string;
      reason: string;
      amount: number;
      due_date: string;
      unit_id?: string;
      booking_id?: string;
      dispute_id?: string;
    }) => {
      if (!activeOrgId) throw new Error("No active organization");

      const res = await supabase.functions.invoke("issue-fine", {
        body: { ...fine, organization_id: activeOrgId },
      });
      // Prefer the specific error from the function body over the generic
      // "non-2xx status code" message supabase-js raises on error responses.
      if (res.error) {
        const detail = (res.data as { error?: string } | null)?.error ?? res.error.message;
        throw new Error(detail);
      }
      return res.data as { fine: Fine; checkoutUrl: string | null };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["fines"] });
      queryClient.invalidateQueries({ queryKey: ["audit_logs", activeOrgId] });
      toast({ title: "Fine issued", description: "The host has been notified." });
    },
    onError: (error: Error) => {
      toast({ title: "Error issuing fine", description: error.message, variant: "destructive" });
    },
  });
}

/** Host disputes their own fine. RLS only allows this exact transition for a host. */
export function useDisputeFine() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, dispute_reason }: { id: string; dispute_reason: string }) => {
      const { error } = await supabase
        .from("fines")
        .update({ status: "disputed", dispute_reason })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["fines"] });
      toast({ title: "Fine disputed", description: "The council/PM will review your dispute." });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });
}

/** Admin/PM resolves a disputed fine, or waives/upholds a fine directly. */
export function useResolveFine() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user, activeOrgId } = useAuth();

  return useMutation({
    mutationFn: async ({
      id,
      status,
      resolution_note,
    }: {
      id: string;
      status: "upheld" | "waived";
      resolution_note?: string;
    }) => {
      const { data, error } = await supabase
        .from("fines")
        .update({ status, resolution_note, resolved_by: user?.id })
        .eq("id", id)
        .select()
        .single();
      if (error) throw error;

      await supabase.from("audit_logs").insert({
        organization_id: activeOrgId,
        action: `Fine ${status}`,
        entity: `Fine ${id}`,
        detail: resolution_note ?? `Fine ${status} by ${user?.fullName ?? "Admin"}`,
        user_name: user?.fullName ?? "Admin",
      });

      return data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["fines"] });
      queryClient.invalidateQueries({ queryKey: ["audit_logs", activeOrgId] });
      toast({ title: `Fine ${variables.status}`, description: `The fine has been ${variables.status}.` });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });
}
